import { FFEScheduleItemStatus, FFEScheduleLeadTime } from "ts-interfaces";

// Public read-only share route for a schedule — the single source of truth
// for the path. Route registration (App.jsx), the HomeGate rewrite, the
// page/header share-mode sniffing and the share dialog's URL builder all
// consume these; never inline the literal.
export const SCHEDULE_SHARE_PATH_PREFIX = "/home/schedules/share/";
export const scheduleSharePath = (scheduleId: string) =>
  `${SCHEDULE_SHARE_PATH_PREFIX}${scheduleId}`;
export const isScheduleSharePath = (pathname: string) =>
  pathname.startsWith(SCHEDULE_SHARE_PATH_PREFIX);

// Beige/editorial palette from the approved schedule mockup. These tokens are
// deliberately local to the schedules feature — the app theme (Inter/#ebebeb)
// does not carry them.
export const SCHED = {
  pageBg: "#F6F5F4",
  cardBg: "#FFFFFF",
  panelBg: "#FAF9F7",
  railBg: "#FBFAF8",
  text: "#1C1B19",
  textSoft: "#4A473F",
  textMuted: "#6B675F",
  textFaint: "#8A867E",
  textDim: "#A9A49B",
  placeholder: "#C9C5BD",
  border: "#E3E0DA",
  borderSoft: "#F0EEE9",
  borderInput: "#D8D4CC",
  hoverBg: "#F4F2EE",
  // Soft green wash for rows just added from the library (return-moment
  // highlight); fades back to cardBg via the row's background transition.
  highlightBg: "#E8F2E9",
  segmentBg: "#E3E0DA",
  chipBg: "#EBE8E2",
  accent: "#C2410C",
  green: "#1F8A5B",
  danger: "#B42318",
  dangerBg: "#FDEEEC",
} as const;

export interface StatusOption {
  label: FFEScheduleItemStatus;
  color: string;
}

// The 12 canonical statuses, grouped exactly as the single
// Specification → Approvals → Procurement pipeline (schema stores the label
// strings verbatim).
export const STATUS_GROUPS: { group: string; options: StatusOption[] }[] = [
  {
    group: "Specification",
    options: [
      { label: "Draft", color: "#8A867E" },
      { label: "Spec'd", color: "#6B675F" },
      { label: "Quoting", color: "#B08968" },
    ],
  },
  {
    group: "Approvals",
    options: [
      { label: "Internal Review", color: "#E8590C" },
      { label: "Client Review", color: "#D97706" },
      { label: "Approved", color: "#1F8A5B" },
      { label: "Revise", color: "#C2255C" },
    ],
  },
  {
    group: "Procurement",
    options: [
      { label: "Ordered", color: "#2A6FDB" },
      { label: "In Production", color: "#7048E8" },
      { label: "Shipped", color: "#0C8599" },
      { label: "Delivered", color: "#2B8A3E" },
      { label: "Installed", color: "#1C1B19" },
    ],
  },
];

export const STATUS_COLORS: Record<string, string> = Object.fromEntries(
  STATUS_GROUPS.flatMap((g) => g.options.map((o) => [o.label, o.color])),
);

export const DEFAULT_STATUS_COLOR = "#8A867E";

export const statusColor = (status?: string): string =>
  (status && STATUS_COLORS[status]) || DEFAULT_STATUS_COLOR;

export const LEAD_TIME_OPTIONS: FFEScheduleLeadTime[] = [
  "In Stock",
  "1-2 weeks",
  "2-4 weeks",
  "4-6 weeks",
  "6-8 weeks",
  "8-10 weeks",
  "10-12 weeks",
  "12-14 weeks",
  "14-16 weeks",
  "16-18 weeks",
];

// Currency symbols/options moved to libs/currency.ts (#3593 — shared with
// the project editor now that currency lives on the project).

// Seed section list for the Section picker. The persisted value is only ever
// the free-text item.categoryOverride — this list is display scaffolding and
// is merged with the sections actually in use (see utils/grouping.ts).
export const DEFAULT_SECTIONS = [
  "Seating",
  "Lighting",
  "Casegoods",
  "Rugs & Flooring",
  "Window Treatments",
  "Textiles & Fabrics",
  "Wall Coverings",
  "Accessories & Decor",
  "Art",
  "Plumbing",
  "Hardware",
  "Appliances",
  "Other",
];

export const SCHEDULE_FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';

// Shared sx fragments (mockup-derived).
export const segBtnSx = {
  textTransform: "none",
  fontFamily: "inherit",
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: 1.45,
  padding: "3px 14px",
  border: "none",
  borderRadius: "6px",
  color: SCHED.textFaint,
  minWidth: 0,
  "&.Mui-selected": {
    background: SCHED.cardBg,
    color: SCHED.text,
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    "&:hover": { background: SCHED.cardBg },
  },
  "&:hover": { background: "transparent" },
} as const;

export const darkBtnSx = {
  textTransform: "none",
  fontFamily: "inherit",
  fontSize: "12.5px",
  fontWeight: 600,
  lineHeight: 1.4,
  padding: "6px 14px",
  borderRadius: "8px",
  background: SCHED.text,
  color: "#FAF9F7",
  boxShadow: "none",
  "&:hover": { background: "#3A3833", boxShadow: "none" },
  // Without this the sx `background` beats MUI's disabled rule, so a disabled
  // button stays black and only the label greys out — unreadable (e.g. the
  // export dialog's "Preparing…" state).
  "&.Mui-disabled": {
    background: SCHED.textFaint,
    color: "#FAF9F7",
    boxShadow: "none",
  },
} as const;

// Secondary sibling of darkBtnSx — same geometry so the two sit as one pair.
export const outlinedBtnSx = {
  ...darkBtnSx,
  // 1px less padding makes room for the border.
  padding: "5px 14px",
  background: "transparent",
  color: SCHED.text,
  border: `1px solid ${SCHED.text}`,
  "&:hover": { background: SCHED.hoverBg, boxShadow: "none" },
  "&.Mui-disabled": {
    background: "transparent",
    color: SCHED.textFaint,
    border: `1px solid ${SCHED.textFaint}`,
  },
} as const;

export const ghostBtnSx = {
  textTransform: "none",
  fontFamily: "inherit",
  fontSize: "12.5px",
  fontWeight: 600,
  lineHeight: 1.4,
  padding: "4px 10px",
  borderRadius: "7px",
  color: SCHED.textMuted,
  minWidth: 0,
  "&:hover": { background: SCHED.hoverBg },
} as const;

export const menuPaperSx = {
  background: SCHED.cardBg,
  border: `1px solid #EAE7E1`,
  borderRadius: "12px",
  boxShadow: "0 14px 40px rgba(0,0,0,0.14)",
  padding: "6px",
} as const;

// Row styling for every "Add Product" menu (schedules, collections, My Library).
export const menuItemSx = {
  borderRadius: "8px",
  fontSize: "13.5px",
  fontFamily: "inherit",
  padding: "8px",
  "&:hover": { background: SCHED.hoverBg },
} as const;

export const dialogPaperSx = {
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
    fontFamily: SCHEDULE_FONT,
    color: SCHED.text,
  },
} as const;

export const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "9px",
    fontSize: "13.5px",
    fontFamily: "inherit",
    color: SCHED.text,
    background: SCHED.cardBg,
  },
  "& .MuiOutlinedInput-input": { padding: "9px 11px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: SCHED.border },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: SCHED.borderInput,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: SCHED.text,
    borderWidth: "1px",
  },
} as const;

export const modalPrimaryBtnSx = {
  width: "100%",
  textTransform: "none",
  fontFamily: "inherit",
  fontSize: "13.5px",
  fontWeight: 600,
  padding: "10px 0",
  borderRadius: "9px",
  background: SCHED.text,
  color: "#FAF9F7",
  boxShadow: "none",
  "&:hover": { background: "#3A3833", boxShadow: "none" },
  // Read as clearly inactive instead of a black button that ignores clicks
  // (#3593 — disabled until the input is a plausible URL).
  "&.Mui-disabled": { background: SCHED.hoverBg, color: SCHED.textDim },
} as const;

export const modalCancelBtnSx = {
  width: "100%",
  textTransform: "none",
  fontFamily: "inherit",
  fontSize: "13.5px",
  fontWeight: 600,
  padding: "10px 0",
  borderRadius: "9px",
  background: SCHED.hoverBg,
  color: SCHED.textSoft,
  boxShadow: "none",
  "&:hover": { background: "#EAE7E1", boxShadow: "none" },
} as const;

export const fieldLabelSx = {
  fontSize: "12px",
  color: SCHED.textMuted,
} as const;

export const colHeaderSx = {
  fontSize: "10.5px",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: SCHED.textDim,
  fontWeight: 600,
} as const;

export const productDetailsSx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: "1000px",
      maxWidth: "94vw",
      height: "86vh",
      ...dialogPaperSx["& .MuiDialog-paper"],
      margin: "16px",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 22px",
    borderBottom: `1px solid ${SCHED.border}`,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
  },
  rail: {
    display: { xs: "none", md: "flex" },
    borderRight: `1px solid ${SCHED.borderSoft}`,
    background: SCHED.railBg,
    padding: "18px 16px",
    flexDirection: "column",
    gap: "14px",
    overflowY: "auto",
  },
  fields: {
    minHeight: 0,
    overflowY: "auto",
    padding: { xs: "18px 16px", md: "22px 26px" },
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "7px 22px",
    borderTop: `1px solid ${SCHED.border}`,
    flexShrink: 0,
    background: SCHED.cardBg,
  },
} as const;
